/**
 * WhatsFreshTodayStrip — a small, low-stakes "what changed recently"
 * strip the user explicitly asked for as an interim step before a
 * true audit-log "Today's Changes" feed.
 *
 * Wave A only uses TIMESTAMPS that ALREADY EXIST on the zone-sheet
 * doc — no new schema, no new endpoint, no per-event audit log. The
 * data ages out automatically because each call passes today's
 * sheet. Sources:
 *   • sheet.manager_notes_updated_at   → "Notes updated"
 *   • sheet.store_focus_updated_at     → "Focus updated"
 *   • summary.items[].verified_at      → latest verified task
 *
 * If there's nothing to show, the strip renders nothing (no empty
 * card noise — the rest of the dashboard already tells the same
 * story).
 */
import { Clock, CheckCircle, Note, Target } from "@phosphor-icons/react";

function shortClock(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function pickLatestVerified(summary) {
  const items = (summary?.items || []).filter(
    (i) => i.status === "manager_verified" && i.verified_at,
  );
  if (!items.length) return null;
  items.sort((a, b) => String(b.verified_at).localeCompare(String(a.verified_at)));
  return items[0];
}

export default function WhatsFreshTodayStrip({
  managerNotesUpdatedAt,
  managerNotesAuthor,
  storeFocusUpdatedAt,
  storeFocusAuthor,
  summary,
}) {
  const latestVerified = pickLatestVerified(summary);
  const chips = [];
  if (managerNotesUpdatedAt) {
    chips.push({
      key: "notes",
      Icon: Note,
      label: "Notes updated",
      detail: `${managerNotesAuthor ? `by ${managerNotesAuthor} · ` : ""}${shortClock(managerNotesUpdatedAt)}`,
      tone: "bg-indigo-50 text-indigo-800 border-indigo-200",
    });
  }
  if (storeFocusUpdatedAt) {
    chips.push({
      key: "focus",
      Icon: Target,
      label: "Focus updated",
      detail: `${storeFocusAuthor ? `by ${storeFocusAuthor} · ` : ""}${shortClock(storeFocusUpdatedAt)}`,
      tone: "bg-amber-50 text-amber-800 border-amber-200",
    });
  }
  if (latestVerified) {
    chips.push({
      key: "verified",
      Icon: CheckCircle,
      label: "Latest verified",
      detail: `${latestVerified.label || "Task"} · ${latestVerified.verified_by_name || "Mgr"} · ${shortClock(latestVerified.verified_at)}`,
      tone: "bg-emerald-50 text-emerald-800 border-emerald-200",
    });
  }
  if (chips.length === 0) return null;
  return (
    <section data-testid="whats-fresh-today" className="rounded-2xl border border-slate-200 bg-white px-4 sm:px-5 py-3 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <Clock weight="bold" className="w-3.5 h-3.5 text-slate-500" />
        <p className="text-[11px] uppercase tracking-[0.18em] font-black text-slate-500">
          What&apos;s Fresh Today
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {chips.map(({ key, Icon, label, detail, tone }) => (
          <span
            key={key}
            className={`inline-flex items-start gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-bold ${tone}`}
            data-testid={`whats-fresh-${key}`}
          >
            <Icon weight="bold" className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span className="min-w-0">
              <span className="block leading-tight">{label}</span>
              <span className="block leading-tight font-normal opacity-80">{detail}</span>
            </span>
          </span>
        ))}
      </div>
    </section>
  );
}
